// PasswordPanels.js - Paneles de recuperación de contraseña
// Se renderizan dentro de LoginScreen (vistas 'forgot' y 'reset'): no traen
// split ni panel de marca, solo el contenido de la columna del formulario.
import React, { useState } from 'react';
import { Eye, EyeOff, Loader2, AlertCircle, MailCheck, CheckCircle2 } from 'lucide-react';
import apiClient from '../services/apiClient';

// ============================================================================
// PANEL 'FORGOT': pide el email y llama a request_password_reset
// ============================================================================
export const ForgotPasswordPanel = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Manejar cambios en el input (limpia los errores al escribir)
  const handleChange = (value) => {
    setEmail(value);
    if (fieldError) setFieldError('');
    if (submitError) setSubmitError('');
  };

  // Validar el email antes de llamar al API
  const validateEmail = () => {
    if (!email.trim()) return 'El email es obligatorio';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'El formato del email no es válido';
    return '';
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = validateEmail();
    if (error) {
      setFieldError(error);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const data = await apiClient.post(
        'request_password_reset',
        { email: email.trim() },
        { skipAuth: true }
      );
      setSuccessMessage(data.message || 'Si el email existe, recibirás un enlace para crear una contraseña nueva.');
    } catch (err) {
      // apiClient lanza HttpError con el envelope del backend en !ok; el resto = red.
      if (err instanceof apiClient.HttpError) {
        setSubmitError(err.payload?.message || 'No hemos podido procesar la solicitud. Inténtalo de nuevo.');
      } else {
        setSubmitError('Error de conexión. Intenta de nuevo.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {successMessage ? (
        /* Estado de éxito: el enlace ya está enviado */
        <div className="login-success">
          <div className="login-success-icon">
            <MailCheck size={22} />
          </div>
          <h2>Revisa tu correo</h2>
          <p>{successMessage}</p>
          <p>Si en unos 5 minutos no lo ves en tu bandeja de entrada, revisa la carpeta de spam.</p>
        </div>
      ) : (
        <>
          <h2>Recuperar contraseña</h2>
          <p className="login-lead">
            Escribe tu email y te enviaremos un enlace para crear una contraseña nueva.
          </p>

          {submitError && (
            <div className="error-banner">
              <AlertCircle size={16} />
              <span>{submitError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="forgot-email" className="form-label">
                Email
              </label>
              <input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => handleChange(e.target.value)}
                className={`form-input ${fieldError ? 'error' : ''}`}
                placeholder="tu@email.com"
                disabled={isSubmitting}
                autoComplete="email"
                autoFocus
              />
              {fieldError && (
                <span className="field-error">{fieldError}</span>
              )}
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="spinning" />
                  Enviando...
                </>
              ) : (
                'Enviarme el enlace'
              )}
            </button>
          </form>
        </>
      )}

      <button type="button" className="login-alt-link" onClick={onBack}>
        ← Volver al login
      </button>
    </>
  );
};

// ============================================================================
// PANEL 'RESET': con el token del mail, guarda la contraseña nueva
// ============================================================================
export const ResetPasswordPanel = ({ token, onBack, onRequestAgain }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirm: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Si el error del backend habla de enlace caducado o inválido, se ofrece pedir otro
  const showRequestAgain = /cadu|no es válido/i.test(submitError);

  // Manejar cambios en los inputs
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    if (submitError) setSubmitError('');
  };

  // Validar formulario
  const validateForm = () => {
    const errors = {};

    if (!formData.password) {
      errors.password = 'La contraseña es obligatoria';
    } else if (formData.password.length < 8) {
      errors.password = 'La contraseña debe tener al menos 8 caracteres';
    }

    if (!formData.confirm) {
      errors.confirm = 'Repite la contraseña';
    } else if (formData.password && formData.confirm !== formData.password) {
      errors.confirm = 'Las contraseñas no coinciden';
    }

    return errors;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const data = await apiClient.post(
        'reset_password',
        { token, password: formData.password },
        { skipAuth: true }
      );
      setSuccessMessage(data.message || 'Tu contraseña se ha actualizado.');
    } catch (err) {
      // apiClient lanza HttpError con el envelope del backend en !ok; el resto = red.
      if (err instanceof apiClient.HttpError) {
        setSubmitError(err.payload?.message || 'No hemos podido cambiar la contraseña. Inténtalo de nuevo.');
      } else {
        setSubmitError('Error de conexión. Intenta de nuevo.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enlace mal formado: ni siquiera se llama al API
  if (!token) {
    return (
      <>
        <h2>Enlace no válido</h2>
        <p className="login-lead">
          Este enlace no es válido. Solicita uno nuevo desde «¿Has olvidado tu contraseña?».
        </p>
        <button type="button" className="login-alt-link" onClick={onRequestAgain}>
          Pedir un enlace nuevo
        </button>
      </>
    );
  }

  // Estado de éxito: contraseña cambiada
  if (successMessage) {
    return (
      <div className="login-success">
        <div className="login-success-icon">
          <CheckCircle2 size={22} />
        </div>
        <h2>Contraseña actualizada</h2>
        <p>{successMessage}</p>
        <button
          type="button"
          className="login-button"
          onClick={onBack}
        >
          Ir al login
        </button>
      </div>
    );
  }

  return (
    <>
      <h2>Nueva contraseña</h2>
      <p className="login-lead">Elige una contraseña nueva para tu cuenta.</p>

      {submitError && (
        <div className="error-banner">
          <AlertCircle size={16} />
          <span>{submitError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="login-form">
        {/* Campo Contraseña nueva */}
        <div className="form-group">
          <label htmlFor="reset-password" className="form-label">
            Nueva contraseña
          </label>
          <div className="password-input-container">
            <input
              id="reset-password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={`form-input ${formErrors.password ? 'error' : ''}`}
              placeholder="••••••••"
              disabled={isSubmitting}
              autoComplete="new-password"
              autoFocus
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isSubmitting}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {formErrors.password && (
            <span className="field-error">{formErrors.password}</span>
          )}
        </div>

        {/* Campo Repetir contraseña */}
        <div className="form-group">
          <label htmlFor="reset-confirm" className="form-label">
            Repítela
          </label>
          <div className="password-input-container">
            <input
              id="reset-confirm"
              type={showConfirm ? 'text' : 'password'}
              value={formData.confirm}
              onChange={(e) => handleInputChange('confirm', e.target.value)}
              className={`form-input ${formErrors.confirm ? 'error' : ''}`}
              placeholder="••••••••"
              disabled={isSubmitting}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirm(!showConfirm)}
              disabled={isSubmitting}
              tabIndex={-1}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {formErrors.confirm && (
            <span className="field-error">{formErrors.confirm}</span>
          )}
        </div>

        <button
          type="submit"
          className="login-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={16} className="spinning" />
              Guardando...
            </>
          ) : (
            'Guardar contraseña'
          )}
        </button>
      </form>

      {showRequestAgain && (
        <button type="button" className="login-alt-link" onClick={onRequestAgain}>
          Pedir un enlace nuevo
        </button>
      )}

      <button type="button" className="login-alt-link" onClick={onBack}>
        ← Volver al login
      </button>
    </>
  );
};
